import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["chip", "count"]
  static classes = ["active", "inactive", "activeCount", "inactiveCount"]
  static values = {
    refreshesCategories: { type: Boolean, default: false },
    preservesScroll: { type: Boolean, default: false },
    scrollKey: String
  }

  initialize() {
    this.preserveScroll = this.preserveScroll.bind(this)
    this.saveScrollPosition = this.saveScrollPosition.bind(this)
  }

  connect() {
    if (!this.preservesScrollValue || !this.hasScrollKeyValue) return

    this.frame = this.element.closest("turbo-frame")
    this.frame?.addEventListener("turbo:before-frame-render", this.preserveScroll)
    this.element.addEventListener("scroll", this.saveScrollPosition, { passive: true })

    this.restoreScrollPosition()
  }

  disconnect() {
    this.frame?.removeEventListener("turbo:before-frame-render", this.preserveScroll)
    this.element.removeEventListener("scroll", this.saveScrollPosition)

    if (this.restoreScrollFrame) {
      cancelAnimationFrame(this.restoreScrollFrame)
    }
  }

  preserveScroll(event) {
    if (event.target !== this.frame) return

    const scrollPosition = this.element.scrollLeft
    const elementId = this.element.id
    const render = event.detail.render

    event.detail.render = (currentElement, newElement) => {
      const result = render(currentElement, newElement)
      const newScrollableElement = currentElement.querySelector(`#${elementId}`)

      if (newScrollableElement) {
        newScrollableElement.scrollLeft = scrollPosition
      }

      return result
    }
  }

  saveScrollPosition() {
    sessionStorage.setItem(this.scrollStorageKey, this.element.scrollLeft)
  }

  restoreScrollPosition() {
    const scrollPosition = sessionStorage.getItem(this.scrollStorageKey)
    if (scrollPosition === null) return

    this.restoreScrollFrame = requestAnimationFrame(() => {
      this.element.scrollLeft = Number(scrollPosition)
    })
  }

  get scrollStorageKey() {
    return `state-chips:${this.scrollKeyValue}:scroll-left`
  }

  select(event) {
    const selected = event.currentTarget
    const alreadySelected = this.activeClasses.every(className => selected.classList.contains(className))
    const shouldDeselect = alreadySelected && selected.dataset.stateChipsDeselectOnActive === "true"

    this.chipTargets.forEach(chip => {
      const active = chip === selected && !shouldDeselect

      chip.classList.remove(...(active ? this.inactiveClasses : this.activeClasses))
      chip.classList.add(...(active ? this.activeClasses : this.inactiveClasses))

      const count = this.countTargets.find(target => chip.contains(target))
      if (count) {
        count.classList.remove(...(active ? this.inactiveCountClasses : this.activeCountClasses))
        count.classList.add(...(active ? this.activeCountClasses : this.inactiveCountClasses))
      }
    })

    if (!alreadySelected || shouldDeselect) {
      this.showLoadingPlaceholder()
      this.showCategoryLoadingPlaceholder()
    }
  }

  showLoadingPlaceholder() {
    const frame = this.element.closest("turbo-frame")
    if (!frame) return

    const products = frame.querySelector("[data-state-chips-products]")
    const template = frame.querySelector("template[data-state-chips-loading-template]")
    if (!products || !template) return

    products.replaceChildren(template.content.cloneNode(true))
  }

  showCategoryLoadingPlaceholder() {
    if (!this.refreshesCategoriesValue) return

    const frame = this.element.closest("turbo-frame")
    if (!frame) return

    const categories = frame.querySelector("[data-state-chips-categories]")
    const template = frame.querySelector("template[data-state-chips-categories-loading-template]")
    if (!categories || !template) return

    categories.replaceChildren(template.content.cloneNode(true))
  }
}
